import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { TemplateBlock } from "@/components/content/TemplateBlock";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Layout, Target, Phone, Star, MapPin, CheckCircle2 } from "lucide-react";

const faqs = [
  {
    question: "What makes a good lawn care homepage?",
    answer: "A good lawn care homepage clearly states what you do, where you serve, and how to contact you—all above the fold. Include trust signals like reviews, service areas, and a strong call-to-action."
  },
  {
    question: "How long should my homepage be?",
    answer: "Aim for 800-1500 words. Cover your main services, service areas, why customers choose you, and include a clear path to your quote form. Don't make visitors scroll endlessly."
  },
  {
    question: "What images should I use on my homepage?",
    answer: "Use real photos of your work, your team, and your equipment. Avoid generic stock photos. Before/after shots work great for lawn care and landscaping."
  },
  {
    question: "Should I put pricing on my homepage?",
    answer: "You can mention starting prices or price ranges to pre-qualify leads. Full pricing details work better on dedicated service pages or a pricing page."
  }
];

const LawnCareHomepageTemplate = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/lawn-care-homepage-template/";
  
  return (
    <>
      <SEO
        title="Lawn Care Homepage Template | Copy-Paste Structure"
        description="Free lawn care homepage template. Copy-paste structure with headlines, sections, and CTAs that convert visitors into lawn care leads."
        url={pageUrl}
        keywords="lawn care homepage template, lawn care website template, lawn service homepage"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Lawn Care Homepage Template"
        pageDescription="Copy-paste homepage template for lawn care websites with proven structure and CTAs."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Website Template</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Lawn Care{" "}
              <span className="text-primary">Homepage Template</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A proven homepage structure for lawn care websites. Copy the layout, 
              customize with your details, and start converting more visitors.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-bold">Homepage Section Structure</h2>
            
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Section 1: Hero (Above the Fold)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Hero Headline Template"
                    content={`[City]'s Most Trusted Lawn Care Service

OR

Professional Lawn Care in [City] – Free Quotes in 24 Hours

OR

Your Lawn, Our Priority. Serving [City] Homeowners Since [Year].`}
                  />
                  <TemplateBlock
                    title="Hero Subheadline"
                    content={`Weekly mowing, fertilization, and lawn treatments that keep your yard looking its best. Locally owned. 5-star rated. Free estimates.`}
                  />
                  <Checklist
                    title="Hero Section Must-Haves"
                    items={[
                      "Phone number visible",
                      "CTA button: 'Get Free Quote' or 'Schedule Service'",
                      "City/service area mentioned",
                      "High-quality hero image (real work, not stock)"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="h-5 w-5 text-primary" />
                    Section 2: Services Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Services Section Headline"
                    content={`Our Lawn Care Services

OR

Complete Lawn Care Solutions for [City] Homes`}
                  />
                  <Checklist
                    title="Services to Feature"
                    items={[
                      "Weekly Lawn Mowing",
                      "Fertilization & Weed Control",
                      "Aeration & Overseeding",
                      "Spring/Fall Cleanup",
                      "Shrub & Hedge Trimming"
                    ]}
                  />
                  <p className="text-muted-foreground text-sm">
                    Link each service to its dedicated service page for SEO.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-primary" />
                    Section 3: Social Proof
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Social Proof Headline"
                    content={`Why [City] Homeowners Choose Us

OR

Join 500+ Happy Customers in [County]`}
                  />
                  <Checklist
                    title="Trust Signals to Include"
                    items={[
                      "Google review count and star rating",
                      "2-3 customer testimonials with names",
                      "Years in business",
                      "Number of lawns serviced",
                      "Licensing/insurance badges"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Section 4: Service Areas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Service Area Section"
                    content={`Proudly Serving [City] and Surrounding Areas

We provide lawn care services throughout [County], including:
• [City 1] • [City 2] • [City 3] • [City 4]
• [Neighborhood 1] • [Neighborhood 2]`}
                  />
                  <p className="text-muted-foreground text-sm">
                    Link to individual service area pages for local SEO boost.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Section 5: Call-to-Action
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Bottom CTA Section"
                    content={`Ready for a Better Lawn?

Get your free lawn care quote today. We'll assess your property and provide transparent pricing—no pressure, no obligations.

[Get Free Quote Button] [Call (XXX) XXX-XXXX Button]`}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Use this template with any website builder",
                "Customize copy with your business details",
                "May take 10-20 hours to implement well",
                "Free if you do it yourself"
              ]}
              doneForYouPoints={[
                "We build your entire site using proven templates",
                "Professional design and copywriting included",
                "Live in 2-3 weeks",
                "$200-300/month with ongoing updates"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Templates"
              links={[
                { to: "/website-templates/lawn-care-service-page-template/", label: "Lawn Care Service Page Template" },
                { to: "/website-templates/service-area-page-template/", label: "Service Area Page Template" },
                { to: "/website-templates/lawn-care-quote-form-template/", label: "Quote Form Template" },
                { to: "/website-templates/lawn-care-website-seo-checklist/", label: "Lawn Care SEO Checklist" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LawnCareHomepageTemplate;
